import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Paper, Typography, Box, Button, TextField, MenuItem, Grid, Alert,
  AlertTitle, LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Collapse, Link, Checkbox, Tooltip,
} from '@mui/material';
import { Upload, ExpandMore, ExpandLess } from '@mui/icons-material';
import axios from 'axios';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'multipart/form-data',
});

const CLASSIFICATION = {
  linked: { label: 'Linked', color: 'success' },
  unmatched_suspect: { label: 'Review', color: 'warning' },
  linked_unexpected: { label: 'Unexpected', color: 'warning' },
  not_specimen: { label: 'Not a specimen', color: 'default' },
};

const MODES = [
  { value: 'whole', label: 'Use the whole value' },
  { value: 'anchor', label: 'The value after the I-number' },
  { value: 'position', label: 'Token at position (split on _)' },
];

const SequencingImport = () => {
  const [file, setFile] = useState(null);
  const [formats, setFormats] = useState([]);
  const [formatOverride, setFormatOverride] = useState('');
  const [runLabel, setRunLabel] = useState('');
  const [runLabelTouched, setRunLabelTouched] = useState(false);
  // specimen-id mapping override
  const [map, setMap] = useState({ manual: false, idColumn: '', mode: '', position: '' });
  const [advanced, setAdvanced] = useState({
    service_request_number: '', flowcell_id: '', base_directory: '', sequencer_type: '',
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState({}); // accepted suggestions: { sample -> specimen_number }

  // Toggle accepting a "did you mean?" suggestion for a review row.
  const toggleAccept = (sample, specimenNumber) => setAccepted(prev => {
    const next = { ...prev };
    if (next[sample] != null) delete next[sample];
    else next[sample] = specimenNumber;
    return next;
  });

  useEffect(() => {
    axios.get('/api/sequencing/formats', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(res => setFormats(res.data.formats || []))
      .catch(() => setFormats([]));
  }, []);

  const buildFormData = (selectedFile, fmt, m) => {
    const fd = new FormData();
    fd.append('file', selectedFile);
    if (fmt) fd.append('format', fmt);
    if (m.manual && m.idColumn) fd.append('id_column', m.idColumn);
    const mode = m.mode || (m.manual ? 'whole' : '');
    if (mode) {
      fd.append('mode', mode);
      if (mode === 'position' && m.position !== '') fd.append('position', m.position);
    }
    return fd;
  };

  const runPreview = useCallback(async (selectedFile, fmt, m) => {
    if (!selectedFile) return;
    // In manual mapping we need a chosen column before previewing.
    if (m.manual && !m.idColumn) { setPreview({ needs_mapping: true, available_columns: preview?.available_columns || [] }); return; }
    setLoading(true); setError(null);
    try {
      const res = await axios.post('/api/sequencing/preview', buildFormData(selectedFile, fmt, m), { headers: authHeaders() });
      const p = res.data.preview;
      setPreview(p);
      setAccepted({}); // suggestions are regenerated per preview
      if (!p.needs_mapping && !runLabelTouched && p.run_label_suggestion) setRunLabel(p.run_label_suggestion);
    } catch (err) {
      setError(err.response?.data?.details || err.response?.data?.error || 'Failed to preview sequencing data');
    } finally {
      setLoading(false);
    }
  }, [runLabelTouched, preview]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    const freshMap = { manual: false, idColumn: '', mode: '', position: '' };
    setFile(selectedFile); setError(null); setResults(null); setPreview(null); setAccepted({});
    setFormatOverride(''); setMap(freshMap);
    runPreview(selectedFile, '', freshMap);
  };

  // Debounce preview so typing a token number (or editing a field) doesn't fire
  // a request per keystroke and doesn't disable the field mid-type.
  const previewTimer = useRef(null);
  const schedulePreview = (selectedFile, fmt, m) => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => runPreview(selectedFile, fmt, m), 300);
  };

  const updateMap = (patch) => {
    const next = { ...map, ...patch };
    setMap(next);
    if (file) schedulePreview(file, formatOverride, next);
  };

  const handleFormatOverride = (value) => {
    setFormatOverride(value);
    if (file) schedulePreview(file, value, map);
  };

  const handleImport = async () => {
    if (!file) { setError('Please select a file to import'); return; }
    setLoading(true); setError(null); setResults(null);
    try {
      const fd = buildFormData(file, formatOverride, map);
      if (runLabel) fd.append('run_label', runLabel);
      Object.entries(advanced).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (Object.keys(accepted).length) fd.append('overrides', JSON.stringify(accepted));
      const res = await axios.post('/api/sequencing/import', fd, { headers: authHeaders() });
      setResults(res.data.results);
      setFile(null); setPreview(null); setAccepted({});
      const input = document.getElementById('file-input');
      if (input) input.value = '';
    } catch (err) {
      setError(err.response?.data?.details || err.response?.data?.error || 'Failed to import sequencing data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanced = (field, value) => setAdvanced(prev => ({ ...prev, [field]: value }));
  const classChip = (c) => CLASSIFICATION[c] || CLASSIFICATION.not_specimen;
  // Accepted suggestions move rows from review -> will-link at import time; reflect
  // that in the preview counts so ticking a box gives immediate feedback.
  const nAccepted = Object.keys(accepted).length;
  const availableColumns = preview?.available_columns || [];
  const needsMapping = preview?.needs_mapping;
  const mapped = preview && !needsMapping;

  // Specimen-id mapping controls (shown for both recognized and unrecognized files).
  const mappingControls = (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px dashed', borderColor: 'grey.400' }}>
      <Typography variant="subtitle2" gutterBottom>Specimen identifier</Typography>
      {mapped && preview.resolution && !map.manual && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Reading from <strong>{preview.resolution.id_column}</strong> → {preview.resolution.description}.{' '}
          <Link component="button" type="button" variant="body2" onClick={() => updateMap({ manual: true, idColumn: preview.resolution.id_column, mode: preview.resolution.mode })}>
            Map a different column
          </Link>
        </Typography>
      )}
      {(map.manual || needsMapping) && (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth select size="small" label="ID column" value={map.idColumn}
              onChange={(e) => updateMap({ manual: true, idColumn: e.target.value })}>
              {availableColumns.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth select size="small" label="Extraction" value={map.mode || 'whole'}
              onChange={(e) => updateMap({ mode: e.target.value })}>
              {MODES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
          </Grid>
          {(map.mode === 'position') && (
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" type="number" label="Token number (1 = first)" value={map.position}
                onChange={(e) => updateMap({ position: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
          )}
        </Grid>
      )}
      {mapped && !map.manual && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField select size="small" label="Extraction" value={preview.resolution.mode}
            onChange={(e) => updateMap({ mode: e.target.value })} sx={{ minWidth: 260 }}>
            {MODES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          {preview.resolution.mode === 'position' && (
            <TextField size="small" type="number" label="Token number (1 = first)" value={map.position}
              onChange={(e) => updateMap({ mode: 'position', position: e.target.value })}
              sx={{ width: 180 }} InputLabelProps={{ shrink: true }} />
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Import Sequencing Data</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Upload a sequencing facility file (CSV, TSV, TXT, or Excel). The format is detected
          automatically; if it isn't recognized, map the sample-identifier column yourself.
          Specimen matching is shown before anything is saved.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <input id="file-input" type="file" accept=".csv,.tsv,.txt,.xlsx,.xls"
            onChange={handleFileChange} style={{ display: 'none' }} disabled={loading} />
          <label htmlFor="file-input">
            <Button variant="outlined" component="span" startIcon={<Upload />} disabled={loading}>Select file</Button>
          </label>
          {file && <Typography variant="body2" sx={{ mt: 1 }}>Selected: {file.name}</Typography>}
        </Box>

        {loading && <Box sx={{ mb: 3 }}><LinearProgress /><Typography variant="body2" sx={{ mt: 1 }}>Working…</Typography></Box>}
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Unrecognized file -> mapping form */}
        {needsMapping && !results && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              We couldn't recognize this file's format. Pick the column that holds the sample identifier
              and how to extract the specimen number from it.
            </Alert>
            {mappingControls}
          </Box>
        )}

        {/* Preview */}
        {mapped && !results && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ mr: 1 }}>Preview</Typography>
              <Chip label={`Format: ${preview.format.display_name}`} color="primary" size="small" />
              <Chip label={preview.operation === 'import_results' ? 'Results → data available' : 'Roster → pending'}
                color={preview.operation === 'import_results' ? 'success' : 'default'} size="small" variant="outlined" />
            </Box>

            {preview.warnings?.map((w, idx) => <Alert severity="warning" sx={{ mb: 1 }} key={idx}>{w}</Alert>)}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h5">{preview.samples}</Typography><Typography variant="body2" color="text.secondary">Samples</Typography></Paper></Grid>
              <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}><Typography variant="h5">{preview.linked + nAccepted}</Typography><Typography variant="body2">Will link{nAccepted > 0 ? ` (+${nAccepted} accepted)` : ''}</Typography></Paper></Grid>
              <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: ((preview.needs_review ?? preview.unmatched_suspect) - nAccepted) > 0 ? 'warning.light' : 'grey.100' }}><Typography variant="h5">{(preview.needs_review ?? preview.unmatched_suspect) - nAccepted}</Typography><Typography variant="body2">Review</Typography></Paper></Grid>
              <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200' }}><Typography variant="h5">{preview.not_specimen}</Typography><Typography variant="body2">Not a specimen</Typography></Paper></Grid>
            </Grid>

            {preview.review_rows?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Rows to review (won't be linked)</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>Sample</TableCell><TableCell>Extracted #</TableCell><TableCell>Link to</TableCell><TableCell align="center">Use it</TableCell><TableCell>Why</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {preview.review_rows.map((r, idx) => {
                        const target = r.suggestion ? r.suggestion.specimen_number : (r.kind === 'out_of_study' ? r.specimen_number : null);
                        return (
                          <TableRow key={idx} selected={accepted[r.sample] != null}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sample}</TableCell>
                            <TableCell>{r.specimen_number ?? '—'}</TableCell>
                            <TableCell>
                              {r.suggestion
                                ? <Box><Chip label={`#${r.suggestion.specimen_number}`} color="info" size="small" /><Typography variant="caption" display="block" color="text.secondary">{r.suggestion.reason}</Typography></Box>
                                : r.kind === 'out_of_study'
                                  ? <Box><Chip label={`#${r.specimen_number}`} color="warning" size="small" variant="outlined" /><Typography variant="caption" display="block" color="text.secondary">exists in {r.project_number ? `project ${r.project_number}` : 'an unknown project'}{r.study_name ? `, study ${r.study_name}` : ', no study'}</Typography></Box>
                                  : '—'}
                            </TableCell>
                            <TableCell align="center">
                              {target != null
                                ? <Tooltip title={`Link this sample to specimen ${target} on import`}>
                                    <Checkbox size="small" checked={accepted[r.sample] != null} onChange={() => toggleAccept(r.sample, target)} disabled={loading} />
                                  </Tooltip>
                                : '—'}
                            </TableCell>
                            <TableCell><Typography variant="caption">{r.reason}</Typography></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {preview.review_truncated && (
                  <Typography variant="caption" color="text.secondary">Showing the first 100. Resolve these, or import and fix the links afterward.</Typography>
                )}
              </Box>
            )}

            <TableContainer component={Paper} sx={{ mb: 1 }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Sample</TableCell><TableCell>Library ID</TableCell><TableCell>Specimen #</TableCell><TableCell>Read depth</TableCell><TableCell>Status</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {preview.sample_previews.map((s, idx) => {
                    const st = classChip(s.classification);
                    return (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.sample}</TableCell>
                        <TableCell>{s.library_id || '—'}</TableCell>
                        <TableCell>{s.specimen_number ?? '—'}</TableCell>
                        <TableCell>{s.read_depth != null ? s.read_depth.toLocaleString() : '—'}</TableCell>
                        <TableCell><Chip label={st.label} color={st.color} size="small" /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {preview.skipped_no_id > 0 && (
              <Typography variant="caption" color="text.secondary">{preview.skipped_no_id} row(s) had no sample identifier and will be skipped.</Typography>
            )}

            {mappingControls}
          </Box>
        )}

        {/* Run details */}
        {file && !results && mapped && (
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Run label" value={runLabel}
                  onChange={(e) => { setRunLabel(e.target.value); setRunLabelTouched(true); }}
                  disabled={loading} InputLabelProps={{ shrink: true }}
                  helperText="Lab-facing handle, e.g. MD1048. Auto-suggested; optional." />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Format" value={formatOverride}
                  onChange={(e) => handleFormatOverride(e.target.value)} disabled={loading}
                  helperText="Auto-detected; override if needed.">
                  <MenuItem value="">Auto-detect</MenuItem>
                  {formats.map(f => <MenuItem key={f.name} value={f.name}>{f.displayName}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>

            <Link component="button" type="button" variant="body2" onClick={() => setShowAdvanced(s => !s)} sx={{ mt: 1, display: 'inline-flex', alignItems: 'center' }}>
              {showAdvanced ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              Advanced (facility IDs &amp; FASTQ path construction)
            </Link>
            <Collapse in={showAdvanced}>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Service request #" value={advanced.service_request_number} onChange={(e) => handleAdvanced('service_request_number', e.target.value)} disabled={loading} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Flowcell ID" value={advanced.flowcell_id} onChange={(e) => handleAdvanced('flowcell_id', e.target.value)} disabled={loading} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Base directory" value={advanced.base_directory} onChange={(e) => handleAdvanced('base_directory', e.target.value)} disabled={loading} helperText="Optional: where this run's files live on HTCF (stored as a reference; paths aren't constructed)." InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Sequencer type" value={advanced.sequencer_type} onChange={(e) => handleAdvanced('sequencer_type', e.target.value)} disabled={loading} InputLabelProps={{ shrink: true }} /></Grid>
              </Grid>
            </Collapse>
          </Box>
        )}

        {file && !results && mapped && (
          <Button variant="contained" color="primary" onClick={handleImport} disabled={loading} sx={{ mb: 3 }}>
            Import sequencing data
          </Button>
        )}

        {/* Results */}
        {results && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Imported run #{results.run_number}{results.run_label ? ` (${results.run_label})` : ''}</AlertTitle>
              {results.created} created, {results.updated} updated via the {results.format} format
              ({results.operation === 'import_results' ? 'results → data available' : 'roster → pending'}).
              {results.overridden > 0 ? ` ${results.overridden} linked via accepted suggestion${results.overridden === 1 ? '' : 's'}.` : ''}
            </Alert>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}><Typography variant="h4">{results.linked}</Typography><Typography variant="body2">Linked</Typography></Paper></Grid>
              <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: (results.no_match + (results.needs_review || 0)) > 0 ? 'warning.light' : 'grey.100' }}><Typography variant="h4">{results.no_match + (results.needs_review || 0)}</Typography><Typography variant="body2">Review</Typography></Paper></Grid>
              <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.200' }}><Typography variant="h4">{results.not_specimen}</Typography><Typography variant="body2">Not a specimen</Typography></Paper></Grid>
              <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}><Typography variant="h4">{results.created + results.updated}</Typography><Typography variant="body2">Total</Typography></Paper></Grid>
            </Grid>
            {results.errors && results.errors.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>Errors ({results.errors.length})</Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Row</TableCell><TableCell>Sample</TableCell><TableCell>Error</TableCell></TableRow></TableHead>
                    <TableBody>
                      {results.errors.map((err, idx) => (
                        <TableRow key={idx}><TableCell>{err.row}</TableCell><TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{err.sample}</TableCell><TableCell>{err.error}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SequencingImport;
